import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { AddCredentialsModal } from '@/components/auth/add-credentials-modal';
import { ThemedText } from '@/components/themed-text';
import { AiConsentCard } from '@/components/assistant/ai-consent-card';
import { InterviewChat } from '@/components/onboarding/interview-chat';
import { InterviewSummary } from '@/components/onboarding/interview-summary';
import { PackPicker } from '@/components/onboarding/pack-picker';
import { Button } from '@/components/ui/button';
import { PressableScale } from '@/components/ui/pressable-scale';
import { FieldBlock } from '@/components/services/wizard/field';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/input';
import {
  useAbandonInterview,
  useInterviewDraft,
  useInterviewStatus,
} from '@/hooks/use-onboarding-interview';
import { useProviderProfile, useUpdateProvider } from '@/hooks/use-provider';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { ONBOARDING_DRAFT_STATUS } from '@/types/onboarding-interview';
import { TEST_TOOLS_ENABLED } from '@/utils/test-tools';
import { FREE_PLAN, PROSE } from '@/content/facts';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { planTierOf } from '@/utils/plan-access';
import { useQuery } from '@tanstack/react-query';
import { IntroOfferStep } from '@/components/onboarding/intro-offer-step';
import { fetchIntroOfferEligible } from '@/services/introOffer';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

const PRIMARY = '#FD5001';

/**
 * Onde o prestador está no cadastro.
 *
 * A configuração do negócio (`interview` e `packs`) e o resumo são o mesmo
 * passo aos olhos dele: conversa, lista de ramos e confirmação são caminhos
 * para a mesma coisa — sair com serviços, agenda e contratos prontos.
 */
type Stage = 'loading' | 'interview' | 'packs' | 'summary' | 'payment' | 'done' | 'plan';

const STAGE_STEP: Record<Stage, number> = {
  loading: 1,
  interview: 1,
  packs: 1,
  summary: 2,
  payment: 3,
  done: 4,
  plan: 5,
};

const TOTAL_STEPS = 5;
// ─── Dados de pagamento ───────────────────────────────────────────────────────

type PaymentStepProps = {
  onSaved: () => void;
};

function StepPaymentData({ onSaved }: PaymentStepProps) {
  const { data: provider } = useProviderProfile();
  const updateProvider = useUpdateProvider();
  const { addToast } = useToastStore();

  const [pixCopyPaste, setPixCopyPaste] = useState(provider?.defaultPixCopyPaste ?? '');
  const [paymentLink, setPaymentLink] = useState(provider?.defaultPaymentLink ?? '');
  const [bankName, setBankName] = useState(provider?.defaultBankDetails?.bankName ?? '');
  const [bankAgency, setBankAgency] = useState(provider?.defaultBankDetails?.bankAgency ?? '');
  const [bankAccount, setBankAccount] = useState(provider?.defaultBankDetails?.bankAccount ?? '');
  const [paymentInstructions, setPaymentInstructions] = useState(
    provider?.defaultBankDetails?.paymentInstructions ?? '',
  );

  const hasValidGroup =
    !!pixCopyPaste.trim() ||
    !!paymentLink.trim() ||
    !!(bankAccount.trim() || bankAgency.trim() || bankName.trim());

  const handleSave = async () => {
    if (!hasValidGroup) {
      addToast('Preencha pelo menos Pix, link de pagamento ou dados bancários.', 'error');
      return;
    }

    try {
      await updateProvider.mutateAsync({
        defaultPixCopyPaste: pixCopyPaste.trim() || undefined,
        defaultPaymentLink: paymentLink.trim() || undefined,
        defaultBankDetails: {
          bankName: bankName.trim() || undefined,
          bankAgency: bankAgency.trim() || undefined,
          bankAccount: bankAccount.trim() || undefined,
          paymentInstructions: paymentInstructions.trim() || undefined,
        },
      });
      addToast('Dados de pagamento salvos!', 'success');
      onSaved();
    } catch {
      addToast('Erro ao salvar dados de pagamento. Tente novamente.', 'error');
    }
  };

  return (
    <KeyboardAwareScrollView
      bottomOffset={KEYBOARD_BOTTOM_OFFSET}
      contentContainerClassName="px-6 py-6 gap-5"
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      <View className="gap-1">
        <ThemedText className="font-bold text-2xl text-foreground">Dados de pagamento</ThemedText>
        <ThemedText className="text-sm leading-5 text-foreground/55">
          Informe como seus clientes devem pagar. Esses dados aparecem nas cobranças
          automaticamente.
        </ThemedText>
      </View>

      {/* Pix */}
      <View className="gap-2">
        <View className="flex-row items-center gap-2">
          <View className="h-7 w-7 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary">
            <Ionicons className="text-primary dark:text-white" name="qr-code-outline" size={14} />
          </View>
          <ThemedText className="font-semibold text-sm text-foreground">Chave Pix</ThemedText>
        </View>
        <Input
          placeholder="Cole aqui seu código Pix ou chave"
          value={pixCopyPaste}
          onChangeText={setPixCopyPaste}
          autoCapitalize="none"
          className="font-mono text-xs"
        />
        <ThemedText className="text-xs text-foreground/45">
          O cliente copia e paga direto pelo Pix.
        </ThemedText>
      </View>

      {/* Link de pagamento */}
      <View className="gap-2">
        <View className="flex-row items-center gap-2">
          <View className="h-7 w-7 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary">
            <Ionicons className="text-primary dark:text-white" name="link-outline" size={14} />
          </View>
          <ThemedText className="font-semibold text-sm text-foreground">
            Link de pagamento
          </ThemedText>
        </View>
        <Input
          placeholder="https://..."
          value={paymentLink}
          onChangeText={setPaymentLink}
          keyboardType="url"
          autoCapitalize="none"
        />
        <ThemedText className="text-xs text-foreground/45">
          PagSeguro, Mercado Pago ou outro link externo.
        </ThemedText>
      </View>

      {/* Dados bancários */}
      <View className="gap-3 rounded-2xl border border-border/50 bg-neutral-50/80 p-4">
        <View className="flex-row items-center gap-2">
          <View className="h-7 w-7 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary">
            <Ionicons className="text-primary dark:text-white" name="card-outline" size={14} />
          </View>
          <ThemedText className="font-semibold text-sm text-foreground">Dados bancários</ThemedText>
          <ThemedText className="text-xs text-foreground/40">(opcional)</ThemedText>
        </View>
        <Input
          placeholder="Nome do banco (ex: Nubank, Itaú)"
          value={bankName}
          onChangeText={setBankName}
        />
        <View className="flex-row gap-2">
          <View className="flex-1">
            <Input
              placeholder="Agência"
              value={bankAgency}
              onChangeText={setBankAgency}
              keyboardType="numeric"
            />
          </View>
          <View className="flex-[2]">
            <Input placeholder="Conta" value={bankAccount} onChangeText={setBankAccount} />
          </View>
        </View>
        <Input
          placeholder="Instruções extras para o cliente..."
          value={paymentInstructions}
          onChangeText={setPaymentInstructions}
          multiline
          numberOfLines={3}
          textAlignVertical="top"
          className="h-auto min-h-[72px] py-3"
        />
      </View>

      <View className="flex-row items-start gap-3 rounded-2xl bg-primary/5 px-4 py-3.5">
        <Ionicons name="information-circle-outline" size={20} color={PRIMARY} />
        <ThemedText className="flex-1 text-xs leading-5 text-foreground/65">
          Preencha pelo menos Pix, link ou conta bancária. Você pode ajustar a qualquer momento.
        </ThemedText>
      </View>

      <Button
        onPress={() => void handleSave()}
        disabled={!hasValidGroup || updateProvider.isPending}
        className="mt-2"
      >
        {updateProvider.isPending ? (
          <View className="flex-row items-center gap-2">
            <ActivityIndicator size="small" color="white" />
            <ThemedText className="text-white">Salvando...</ThemedText>
          </View>
        ) : (
          <ThemedText className="font-semibold text-white">Salvar e continuar</ThemedText>
        )}
      </Button>

      {TEST_TOOLS_ENABLED && (
        <Button
          variant="outline"
          onPress={onSaved}
          disabled={updateProvider.isPending}
          className="mt-1 border-dashed border-neutral-300"
        >
          <View className="flex-row items-center gap-2">
            <Ionicons className="text-neutral-500" name="construct-outline" size={16} />
            <ThemedText className="font-semibold text-sm text-neutral-500">
              Prestador vai preencher depois (teste)
            </ThemedText>
          </View>
        </Button>
      )}
    </KeyboardAwareScrollView>
  );
}

// ─── Recebimento automático ───────────────────────────────────────────────────

/**
 * Quem escolheu o automático na conversa não preenche Pix aqui: o cliente paga
 * pelo link da cobrança. A ativação (dados e conta do banco) vem logo depois
 * do cadastro, na tela de pagamentos — o onboarding só avisa o que vem.
 */
function StepAutomaticPayments({ onContinue }: { onContinue: () => void }) {
  return (
    <KeyboardAwareScrollView
      keyboardShouldPersistTaps="handled"
      bottomOffset={KEYBOARD_BOTTOM_OFFSET}
      contentContainerClassName="px-6 py-6 gap-5"
      showsVerticalScrollIndicator={false}
    >
      <View className="gap-1">
        <ThemedText className="font-bold text-2xl text-foreground">
          Recebimento automático
        </ThemedText>
        <ThemedText className="text-sm leading-5 text-foreground/55">
          Assim que terminar o cadastro, você ativa o recebimento. Leva uns 2 minutos.
        </ThemedText>
      </View>

      <View className="gap-2 rounded-2xl border border-border/40 bg-card p-4">
        {[
          'CPF, data de nascimento e endereço',
          'A conta do banco onde o dinheiro vai cair',
          'Tudo dentro do app',
        ].map((item) => (
          <View key={item} className="flex-row items-center gap-3">
            <Ionicons name="checkmark-circle-outline" size={16} color={PRIMARY} />
            <ThemedText className="text-sm text-foreground/70">{item}</ThemedText>
          </View>
        ))}
      </View>

      <Button onPress={onContinue} className="mt-2">
        <ThemedText className="font-semibold text-white">Continuar</ThemedText>
      </Button>
    </KeyboardAwareScrollView>
  );
}

// ─── Tudo pronto ──────────────────────────────────────────────────────────────

/**
 * "Como conheceu?" — único sinal de origem de quem baixou o app (a App Store
 * não conta ao app de onde a pessoa veio). Opcional: não barra o fim.
 */
const HEARD_FROM_OPTIONS = [
  { value: 'google', label: 'Google' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'app_store', label: 'Loja de apps' },
  { value: 'indicacao', label: 'Indicação' },
  { value: 'outro', label: 'Outro' },
] as const;

type HeardFrom = (typeof HEARD_FROM_OPTIONS)[number]['value'];

function HeardFromField({
  value,
  onChange,
}: {
  value: HeardFrom | null;
  onChange: (value: HeardFrom) => void;
}) {
  return (
    <View className="w-full rounded-2xl border border-neutral-900/5 bg-card p-4">
      <FieldBlock title="Como conheceu a Controlapag?" note="Opcional">
        <View className="flex-row flex-wrap gap-2">
          {HEARD_FROM_OPTIONS.map((option) => {
            const active = value === option.value;
            return (
              <PressableScale
                key={option.value}
                haptic
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                onPress={() => onChange(option.value)}
                className={cn(
                  'h-9 items-center justify-center rounded-full px-3.5',
                  active ? 'bg-primary' : 'bg-neutral-100',
                )}
                style={{ borderCurve: 'continuous' }}
              >
                <ThemedText
                  className={cn(
                    'font-semibold text-[13px]',
                    active ? 'text-primary-foreground' : 'text-neutral-700',
                  )}
                >
                  {option.label}
                </ThemedText>
              </PressableScale>
            );
          })}
        </View>
      </FieldBlock>
    </View>
  );
}

function StepDone({
  automatic,
  heardFrom,
  onHeardFromChange,
}: {
  automatic: boolean;
  heardFrom: HeardFrom | null;
  onHeardFromChange: (value: HeardFrom) => void;
}) {
  return (
    <KeyboardAwareScrollView
      bottomOffset={KEYBOARD_BOTTOM_OFFSET}
      contentContainerClassName="flex-grow items-center justify-center gap-6 px-6 py-8"
      showsVerticalScrollIndicator={false}
    >
      <View className="h-24 w-24 items-center justify-center rounded-full bg-green-100 dark:bg-success-500">
        <Ionicons className="text-success-500 dark:text-white" name="checkmark-circle" size={52} />
      </View>
      <View className="items-center gap-3">
        <ThemedText className="text-center font-bold text-3xl text-foreground">
          Tudo pronto!
        </ThemedText>
        <ThemedText className="text-center text-base leading-6 text-foreground/60">
          Sua conta está configurada. Agora você pode criar seus serviços, cadastrar clientes e
          começar a cobrar de forma organizada.
        </ThemedText>
      </View>
      <View className="w-full gap-2 rounded-2xl border border-border/40 bg-card p-4">
        {[
          'Serviços e agenda configurados',
          automatic
            ? 'Próximo passo: ativar o recebimento automático'
            : 'Dados de pagamento salvos',
          'Tudo pronto para criar contratos e cobranças',
        ].map((item, i) => (
          <View key={i} className="flex-row items-center gap-3">
            <Ionicons className="text-success-500" name="checkmark-circle" size={16} />
            <ThemedText className="text-sm text-foreground/70">{item}</ThemedText>
          </View>
        ))}
      </View>
      <HeardFromField value={heardFrom} onChange={onHeardFromChange} />
    </KeyboardAwareScrollView>
  );
}

// ─── Plano ────────────────────────────────────────────────────────────────────

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Último passo: diz com clareza o que fica no gratuito e o que é dos planos,
 * antes de a pessoa descobrir esbarrando num cadeado. Na loja da Apple o preço
 * vem da própria loja, então ali não aparece valor.
 */
function StepPlan() {
  return (
    <KeyboardAwareScrollView
      bottomOffset={KEYBOARD_BOTTOM_OFFSET}
      contentContainerClassName="px-6 py-6 gap-5"
      showsVerticalScrollIndicator={false}
    >
      <View className="gap-1">
        <ThemedText className="font-bold text-2xl text-foreground">Seu plano</ThemedText>
        <ThemedText className="text-sm leading-5 text-foreground/55">
          Você começa no gratuito, com até {FREE_PLAN.maxClientsPerMonth} clientes por mês.
        </ThemedText>
      </View>

      <View className="gap-2 rounded-2xl border border-border/40 bg-card p-4">
        <ThemedText className="font-semibold text-[15px] text-foreground">No gratuito</ThemedText>
        {FREE_PLAN.includes.slice(0, 5).map((item) => (
          <View key={item} className="flex-row items-center gap-3">
            <Ionicons className="text-success-500" name="checkmark-circle" size={16} />
            <ThemedText className="flex-1 text-sm text-foreground/70">
              {capitalize(item)}
            </ThemedText>
          </View>
        ))}
      </View>

      <View className="gap-2 rounded-2xl border border-primary/30 bg-primary/5 p-4">
        <View className="flex-row items-center justify-between gap-3">
          <ThemedText className="font-semibold text-[15px] text-foreground">
            Só nos planos pagos
          </ThemedText>
          {Platform.OS !== 'ios' && (
            <ThemedText className="text-xs text-foreground/55">
              {capitalize(PROSE.fromPrice)}
            </ThemedText>
          )}
        </View>
        {FREE_PLAN.paidOnly.map((item) => (
          <View key={item} className="flex-row items-center gap-3">
            <Ionicons name="star-outline" size={16} color={PRIMARY} />
            <ThemedText className="flex-1 text-sm text-foreground/70">
              {capitalize(item)}
            </ThemedText>
          </View>
        ))}
      </View>
    </KeyboardAwareScrollView>
  );
}

// ─── Onboarding principal ─────────────────────────────────────────────────────

/**
 * O cadastro do prestador — **uma conversa, não um formulário**.
 *
 * A tela abre com o agente perguntando o que ele faz; ao fim, o resumo mostra
 * o que vai ser criado e "Confirmar" dispara o `apply()`. Só depois disso vêm
 * os dados de pagamento e o fecho.
 *
 * Três saídas, todas sempre disponíveis, porque nenhuma delas pode deixar
 * alguém preso no cadastro:
 * - **agente fora** (`503`) ou conversa travada: a lista de ramos curados
 *   (T-24) preenche a mesma coisa sem IA nenhuma;
 * - **"prefiro configurar sozinho"**: abandona o rascunho e vai direto para o
 *   pagamento — as telas normais do app continuam lá;
 * - **"ajustar"** no resumo: volta para a conversa, nunca para um formulário.
 */
export default function OnboardingScreen() {
  const [stage, setStage] = useState<Stage>('loading');
  /** O agente caiu no meio da conversa: muda o texto da lista de ramos. */
  const [interrupted, setInterrupted] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  /** Quem entrou só pelo WhatsApp ganha a oferta de email+senha ao fechar. */
  const [showCredentials, setShowCredentials] = useState(false);
  const [heardFrom, setHeardFrom] = useState<HeardFrom | null>(null);

  const status = useInterviewStatus();
  const draft = useInterviewDraft();
  const abandon = useAbandonInterview();
  const updateProvider = useUpdateProvider();
  const { fetchProfile, user } = useAuthStore();
  const { addToast } = useToastStore();

  /** A rota de entrada é decidida uma vez; depois quem manda é a navegação. */
  const routed = useRef(false);

  useEffect(() => {
    if (routed.current) return;
    if (status.isLoading || draft.isLoading) return;
    routed.current = true;

    // Retomada (cenário 7): o rascunho diz em que ponto o cadastro parou.
    const draftStatus = draft.data?.status;
    if (
      draftStatus === ONBOARDING_DRAFT_STATUS.APPLIED ||
      draftStatus === ONBOARDING_DRAFT_STATUS.ABANDONED
    ) {
      setStage('payment');
      return;
    }
    if (draftStatus === ONBOARDING_DRAFT_STATUS.READY) {
      setStage('summary');
      return;
    }
    setStage(status.data?.agentAvailable ? 'interview' : 'packs');
  }, [draft.data?.status, draft.isLoading, status.data, status.isLoading]);

  /** "Prefiro configurar sozinho": encerra sem deixar nada para trás. */
  const handleSkip = async () => {
    try {
      await abandon.mutateAsync();
    } catch {
      // Abandonar é higiene do rascunho, não pré-requisito do cadastro: se
      // falhar, o prestador segue mesmo assim.
    }
    setStage('payment');
  };

  /** Escolheu o automático na conversa: a configuração vem logo depois. */
  const automatic = draft.data?.payload?.paymentPreference === 'automatic';

  const goHome = () =>
    router.replace(automatic ? '/(tabs)/(provider)/payments/activate' : '/(tabs)/(provider)/home');

  /** Quem já tem plano pago (ou é isento) não precisa da oferta. */
  const finishOrOfferPlan = () => {
    const tier = planTierOf(useAuthStore.getState().user);
    if (tier === 'FREE') setStage('plan');
    else goHome();
  };

  // Quem ainda pode pegar o 1º mês do Pro por R$ 1,99 vê a oferta; quem já
  // usou (ou a loja não respondeu) vê o comparativo gratuito × planos.
  const introOffer = useQuery({
    queryKey: ['intro-offer-eligible'],
    queryFn: () => fetchIntroOfferEligible().catch(() => false),
    enabled: stage === 'plan',
    staleTime: Infinity,
  });
  const showIntroOffer = introOffer.data === true;
  const freeLabel = automatic ? 'Depois, ativar recebimento' : 'Seguir no gratuito';

  const seePlans = async () => {
    await navigateToSubscriptionPlans({ push: (path) => router.replace(path as never) });
    // Android abre o checkout no navegador: o app segue para a home por trás.
    if (Platform.OS === 'android') goHome();
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      await updateProvider.mutateAsync({
        onboardingCompleted: true,
        ...(heardFrom && { heardFrom }),
      } as any);
      await fetchProfile();

      // A conta que nasceu só com o telefone ainda não tem como ser recuperada
      // se o número mudar. Este é o momento de oferecer — com o cadastro
      // pronto, e ainda dentro do fluxo, não perdido num menu depois.
      if (!user?.email) {
        setShowCredentials(true);
        return;
      }

      finishOrOfferPlan();
    } catch {
      addToast('Erro ao finalizar onboarding. Tente novamente.', 'error');
    } finally {
      setIsCompleting(false);
    }
  };

  const step = STAGE_STEP[stage];
  const progressPercent = (step / TOTAL_STEPS) * 100;

  return (
    <SafeAreaView className="flex-1 bg-background">
      {/* Header / Progress */}
      <View className="gap-3 px-6 pb-2 pt-4">
        <View className="flex-row items-center justify-between">
          <ThemedText className="font-medium text-xs uppercase tracking-widest text-foreground/40">
            Configuração inicial
          </ThemedText>
          <ThemedText className="font-semibold text-xs text-foreground/50">
            {step}/{TOTAL_STEPS}
          </ThemedText>
        </View>
        <View className="h-1.5 w-full rounded-full bg-border/40">
          <View
            className="h-1.5 rounded-full bg-primary"
            style={{ width: `${progressPercent}%` }}
          />
        </View>
      </View>

      <View className="min-h-0 flex-1">
        {stage === 'loading' && (
          <View className="flex-1 items-center justify-center">
            <ActivityIndicator color={PRIMARY} />
          </View>
        )}

        {stage === 'interview' && !status.data?.aiConsent && (
          <AiConsentCard
            onDecline={() => setStage('packs')}
            declineLabel="Prefiro escolher na lista"
          />
        )}

        {stage === 'interview' && !!status.data?.aiConsent && (
          <InterviewChat
            brandColor={PRIMARY}
            onFinished={() => setStage('summary')}
            onExhausted={() => {
              setInterrupted(true);
              setStage('packs');
            }}
            onAgentDown={() => {
              setInterrupted(true);
              setStage('packs');
            }}
          />
        )}

        {stage === 'packs' && (
          <PackPicker
            interrupted={interrupted}
            onAdopted={() => setStage('summary')}
            onSkip={() => void handleSkip()}
          />
        )}

        {stage === 'summary' && (
          <InterviewSummary
            onApplied={() => setStage('payment')}
            // Sem agente não há conversa para onde voltar: o ajuste vira
            // escolher outro ramo na lista.
            onAdjust={
              status.data?.agentAvailable && !interrupted ? () => setStage('interview') : undefined
            }
            onPickAnother={
              status.data?.agentAvailable && !interrupted ? undefined : () => setStage('packs')
            }
          />
        )}

        {stage === 'payment' &&
          (automatic ? (
            <StepAutomaticPayments onContinue={() => setStage('done')} />
          ) : (
            <StepPaymentData onSaved={() => setStage('done')} />
          ))}

        {stage === 'plan' &&
          (introOffer.isPending ? (
            <View className="flex-1 items-center justify-center">
              <ActivityIndicator color={PRIMARY} />
            </View>
          ) : showIntroOffer ? (
            <IntroOfferStep freeLabel={freeLabel} onDone={goHome} />
          ) : (
            <StepPlan />
          ))}

        {stage === 'done' && (
          <StepDone automatic={automatic} heardFrom={heardFrom} onHeardFromChange={setHeardFrom} />
        )}
      </View>

      {stage === 'done' && (
        <View className="px-6 pb-4 pt-2">
          <Button onPress={() => void handleComplete()} disabled={isCompleting} className="w-full">
            {isCompleting ? (
              <View className="flex-row items-center gap-2">
                <ActivityIndicator size="small" color="white" />
                <ThemedText className="font-semibold text-white">Finalizando...</ThemedText>
              </View>
            ) : (
              <ThemedText className="font-semibold text-white">Continuar</ThemedText>
            )}
          </Button>
        </View>
      )}

      {stage === 'plan' && !introOffer.isPending && !showIntroOffer && (
        <View className="gap-2 px-6 pb-4 pt-2">
          <Button onPress={() => void seePlans()} className="w-full">
            <ThemedText className="font-semibold text-white">Ver os planos</ThemedText>
          </Button>
          <Button variant="ghost" onPress={goHome} className="w-full">
            <ThemedText className="font-semibold text-foreground/70">{freeLabel}</ThemedText>
          </Button>
        </View>
      )}

      <AddCredentialsModal
        open={showCredentials}
        onDismiss={() => {
          setShowCredentials(false);
          finishOrOfferPlan();
        }}
      />
    </SafeAreaView>
  );
}
